import React, { useState } from 'react';
import dayjs from 'dayjs';
import { Page, Text, View, Document, StyleSheet, pdf } from '@react-pdf/renderer';
import { useAppSelector } from '~redux/hooks';
import ActionButton from '~components/atoms/ActionButton';
import { TInventoryTransferData } from '~types/feat/gold';
import { FundTypeEnum } from '~utils/enums';

type PageProps = {
    children: React.ReactNode;
    title: string;
    subtitle: string;
    copy: string;
}

type ReportsInventoryPDFProps = {
    inventoryDeliveredListing: TInventoryTransferData[];
}

const InventoryDeliveredExportPDF = (props: ReportsInventoryPDFProps) => {
    const { inventoryDeliveredListing } = props;

    const branchListing = useAppSelector((state) => state.branch.allBranches);
    const userDetails = useAppSelector((state) => state.auth.userDetails);

    const [loading, setLoading] = useState(false);

    const userBranchDetails = branchListing.find(branch => branch.branchCode === userDetails.branchCode);

    const productTableData = inventoryDeliveredListing.map(item => {
        const serialNumbers = item.serialNumbers?.map(s => s.serialNo).filter(Boolean).join(', ') || '-';
        return {
            productName: item.product.productName,
            serialNo: serialNumbers,
            quantity: item.quantity,
            totalGoldWeight: item.totalGoldWeight,
            fundType: FundTypeEnum[item.fundType],
            deliveredTo: item.to.branchName,
        };
    });

    const customerDetails = {
        name: userDetails.username,
        idNo: userDetails.userId,
    };
    const PageWithHeaderFooter = ({
        children,
        title,
        subtitle,
        copy,
    }: PageProps) => (
        <Page size='A4' style={copy === 'Customer\'s Copy' ? styles.customerPage : styles.page} orientation='landscape'>
            {/* Header */}
            <View style={styles.header}>
                <View style={styles.headerContent}>
                    <Text style={styles.headerTitle}>{title}</Text>
                </View>

                <View style={styles.detailsTable}>
                    <View style={styles.detailsRow}>
                        <Text style={styles.detailsCell}>Branch Name</Text>
                        <Text style={styles.detailsCell}>{userBranchDetails?.branchName}</Text>
                    </View>

                    <View style={styles.detailsRow}>
                        <Text style={styles.detailsCell}>Branch Code</Text>
                        <Text style={styles.detailsCell}>{userBranchDetails?.branchCode}</Text>
                    </View>

                    <View style={styles.detailsRow}>
                        <Text style={styles.detailsCell}>Date and Time</Text>
                        <Text style={styles.detailsCell}>{dayjs().format('DD MMM YYYY hh:mm A')}</Text>
                    </View>
                </View>
            </View>

            {/* Main Content */}
            <View style={styles.mainContent}>
                {children}
            </View>
        </Page>
    );
    const renderTable = () => {
        return (
            <View style={styles.table}>
                <View style={styles.tableRow}>
                    <Text style={[styles.cell_csv, styles.header_csv, { flex: 2 }]}>PRODUCT NAME</Text>
                    <Text style={[styles.cell_csv, styles.header_csv, { flex: 2 }]}>DELIVERED TO</Text>
                    <Text style={[styles.cell_csv, styles.header_csv, { flex: 3 }]}>SERIAL NUMBER</Text>
                    <Text style={[styles.cell_csv, styles.header_csv, { flex: 1 }]}>QUANTITY</Text>
                    <Text style={[styles.cell_csv, styles.header_csv, { flex: 2 }]}>TOTAL GOLD WEIGHT (g)</Text>
                    <Text style={[styles.cell_csv, styles.header_csv, { flex: 1 }]}>FUND TYPE</Text>
                </View>

                {productTableData.map((item, index) => (
                    <View key={index} style={styles.tableRow}>
                        <Text style={[styles.cell_csv, { flex: 2 }]}>{item.productName}</Text>
                        <Text style={[styles.cell_csv, { flex: 2 }]}>{item.deliveredTo}</Text>
                        <Text style={[styles.cell_csv, { flex: 3 }]}>{item.serialNo}</Text>
                        <Text style={[styles.cell_csv, { flex: 1, textAlign: 'center' }]}>{item.quantity ?? '-'}</Text>
                        <Text style={[styles.cell_csv, { flex: 2, textAlign: 'center' }]}>{item.totalGoldWeight ?? '-'}</Text>
                        <Text style={[styles.cell_csv, { flex: 1 }]}>{item.fundType}</Text>
                    </View>
                ))}
            </View>
        );
    };

    const handleOpenPDFInNewTab = () => {
        setLoading(true);
        // Create the PDF document

        const docTitle = 'Branch Physical Inventory Report';

        const doc = (
            <Document>
                <PageWithHeaderFooter
                    title={docTitle}
                    subtitle='SUMMARY DETAILS / RINGKASAN'
                    copy={'Customer\'s Copy'}
                >
                    {renderTable()}
                </PageWithHeaderFooter>
            </Document>
        );

        // Generate PDF Blob and open in new tab
        pdf(doc).toBlob().then(() => {
            const promise = pdf(doc).toBlob();
            promise.then((blob) => {
                const pdfUrl = URL.createObjectURL(blob);
                setLoading(false);
                window.open(pdfUrl, '_blank');
            }).catch(() => {
                setLoading(false);
            });
        });
    };
    return (
        <div>
            <ActionButton
                label='Export to PDF'
                onClick={handleOpenPDFInNewTab}
                isLoading={loading}
                // disabled={inventoryReceivedListing.length < 1}
            // className={SummaryStyles.container}
            />
        </div>
    );
};

const styles = StyleSheet.create({
    page: {
        flexDirection: 'column',
        paddingTop: 50, // Space for header
        // paddingBottom: 80, // Space for footer
        paddingHorizontal: 20,
        position: 'relative',
    },
    customerPage: {
        flexDirection: 'column',
        paddingTop: 100, // Space for header
        // paddingBottom: 80, // Space for footer
        paddingHorizontal: 20,
        position: 'relative',
    },
    header: {
        position: 'absolute',
        top: 10,
        left: 20,
        right: 20,
        display: 'flex',
        flexDirection: 'column',
    },
    headerLogo: {
        width: 162,
        height: 50,
        padding: 5,
    },
    headerContent: {
        flexDirection: 'column',
        gap: 3,
        marginBottom: 5,
    },
    headerTitle: {
        fontSize: 18,
        fontFamily: 'Helvetica-Bold',
    },
    headerSubtitle: {
        fontSize: 16,
        fontFamily: 'Helvetica-Bold',
        paddingBottom: 10,
    },
    headerCopy: {
        position: 'absolute',
        top: 5,
        right: 20,
        fontSize: 6,
        color: '#666',
    },
    divider: {
        height: 2,
        backgroundColor: '#000',
        marginVertical: 3,
    },
    footer: {
        position: 'absolute',
        bottom: 20,
        left: 20,
        right: 20,
        display: 'flex',
        flexDirection: 'column',
    },
    footerContent: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: 10,
    },
    pageNumber: {
        fontSize: 6,
        color: '#666',
    },
    mainContent: {
        flex: 1,
    },
    detailsTable: {
        display: 'flex',
        width: '100%',
        paddingVertical: 15,
        paddingHorizontal: 10,
        fontSize: 10,
        fontFamily: 'Helvetica-Bold',
        gap: 2,
        marginBottom: 15,
    },
    detailsRow: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    detailsCell: {
        width: '50%',
    },
    summaryContainer: {
        marginBottom: 20,
    },
    summaryTable: {
        width: '100%',
        borderStyle: 'solid',
        borderColor: '#D4D4D8',
        borderTopWidth: 2,
        fontSize: 8,
    },
    summaryTableRow: {
        flexDirection: 'row',
        paddingLeft: 15,
        borderBottomWidth: 2,
        borderColor: '#D4D4D8',
    },
    summaryTableCell: {
        paddingVertical: 5,
        textAlign: 'center',
    },
    summaryHeaderCell: {
        width: '60%', // First column
        paddingVertical: 5,
        textAlign: 'center',
        fontFamily: 'Helvetica-Bold',
    },
    summaryHeaderCellPrice: {
        width: '20%', // Second column
        paddingVertical: 5,
        textAlign: 'center',
        fontFamily: 'Helvetica-Bold',
    },
    remarksContainer: {
        display: 'flex',
        width: '100%',
        height: 30,
        fontSize: 6,
        gap: 1,
        marginBottom: 10,
        borderStyle: 'solid',
        borderWidth: 1,
        borderColor: '#000000',
    },
    signatureContainer: {
        // display: 'flex',
        width: '100%',
        fontSize: 6,
        gap: 1,
        marginBottom: 20,
    },
    signatureTable: {
        display: 'flex',
        flexDirection: 'row',
        width: '100%',
        fontSize: 6,
        gap: 1,
        marginBottom: 20,
    },
    signatureRow: {
        width: '25%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
    },
    signatureBox: {
        paddingTop: 30,
    },
    table: {
        width: '100%',
        borderWidth: 1,
        borderColor: '#000',
        marginTop: 20,
    },

    tableRow: {
        flexDirection: 'row',
        borderBottomWidth: 1,
        borderColor: '#000',
    },

    cell_csv: {
        padding: 6,
        borderRightWidth: 1,
        borderColor: '#000',
        fontSize: 9,
    },

    header_csv: {
        fontFamily: 'Helvetica-Bold',
    },
});

export default InventoryDeliveredExportPDF;
